import { useState, useEffect } from 'react';
import { Button, Form, Modal } from "react-bootstrap";
import { Autocomplete, TextField, Tooltip, IconButton, InputAdornment } from "@mui/material";
import { HelpCenter } from "@mui/icons-material";
import useAxiosProtect from '../../../../hooks/useAxiosProtect';
import useAuth from '../../../../hooks/useAuth';

const yesNo = ['Yes', 'No'];
const economizerTypeOptions = ['Boiler Makeup Water', 'Boiler Feedwater', 'Process Stream'];

const ECON_TYPE_KEY = {
  'Boiler Makeup Water': 'bmw',
  'Boiler Feedwater': 'bfw',
  'Process Stream': 'ps',
};

const getPFDImageKey = (config) => {
  if (!config) return null;
  const econ = config.economizer === 'Yes' ? (ECON_TYPE_KEY[config.economizerType] ?? null) : 'n';
  if (!econ) return null;
  const bcw = config.blowdownCoolingWater === 'Yes' ? 'y' : 'n';
  const cr = config.condensateReturn === 'Yes' ? 'y' : 'n';
  const dsi = config.deaeratorSteamInjection === 'Yes' ? 'y' : 'n';
  return `${econ}_${bcw}_${cr}_${dsi}`;
};

const tooltips = {
  economizer: 'Whether the boiler has an economizer installed',
  economizerType: 'The type of economizer installed on the boiler',
  blowdownCoolingWater: 'Whether the boiler uses blowdown cooling water',
  condensateReturn: 'Whether condensate is returned to the boiler system',
  deaeratorSteamInjection: 'Whether the deaerator uses steam injection',
  blowdownRate: 'Percentage of boiler makeup water that is continuously blown down to control dissolved solids',
  blowdownFlashSteamRate: 'Percentage of blowdown liquid that flashes to steam when pressure is reduced',
  condensateFlashSteamRate: 'Percentage of condensate that flashes to steam when returned to a lower-pressure vessel',
  blowdownWaterTemp: 'Temperature of the blowdown water discharged from the boiler',
  daVentSystem: 'Amount of steam vented from the deaerator to remove dissolved oxygen and non-condensable gases',
  steamPressure: 'Operating steam pressure at the boiler outlet',
  condensateReturnTemp: 'Temperature of the condensate as it returns to the boiler system',
};

const FieldRow = ({ children, tooltip }) => (
  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
    <div style={{ flex: 1 }}>{children}</div>
    {tooltip ? (
      <Tooltip title={tooltip} slotProps={{ popper: { sx: { zIndex: 1000000 } } }}>
        <IconButton sx={{ m: 1 }} style={{ backgroundColor: 'white' }}>
          <HelpCenter />
        </IconButton>
      </Tooltip>
    ) : (
      <div style={{ width: 40, margin: 8 }} />
    )}
  </div>
);

const NumericField = ({ label, value, onChange, unit, tooltip }) => (
  <FieldRow tooltip={tooltip}>
    <TextField
      label={label}
      value={value}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '' || /^\d*\.?\d*$/.test(v)) onChange(v);
      }}
      variant="outlined"
      fullWidth
      inputProps={{ inputMode: 'decimal' }}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
    />
  </FieldRow>
);

const ReviewRow = ({ label, value, unit }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, padding: '4px 0', borderBottom: '1px solid #eee' }}>
    <span style={{ color: '#555', fontSize: 14 }}>{label}</span>
    <span style={{ fontWeight: 500, fontSize: 14 }}>{value}{unit ? ` ${unit}` : ''}</span>
  </div>
);

const ReviewSection = ({ title }) => (
  <p style={{ fontWeight: 600, marginTop: 16, marginBottom: 8, fontSize: 13, textTransform: 'uppercase', color: '#888' }}>{title}</p>
);

const BoilerModal = ({ show, onHide, onConfirm, initialConfig }) => {
  const [page, setPage] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const axiosProtect = useAxiosProtect();
  const { auth } = useAuth();

  // Sensor lists
  const [waterMeters, setWaterMeters] = useState([]);
  const [gasSensors, setGasSensors] = useState([]);
  const [ctSensors, setCtSensors] = useState([]);

  // Page 1
  const [economizer, setEconomizer] = useState(null);
  const [economizerType, setEconomizerType] = useState(null);
  const [blowdownCoolingWater, setBlowdownCoolingWater] = useState(null);
  const [condensateReturn, setCondensateReturn] = useState(null);
  const [deaeratorSteamInjection, setDeaeratorSteamInjection] = useState(null);
  const [boilerMakeupWaterTemp, setBoilerMakeupWaterTemp] = useState('');
  const [feedwaterTemp, setFeedwaterTemp] = useState('');
  const [heatCapacity, setHeatCapacity] = useState('');

  // Page 2 — Assign Sensors
  const [blowdownCoolingWaterMeter, setBlowdownCoolingWaterMeter] = useState(null);
  const [economizerInletTemp, setEconomizerInletTemp] = useState(null);
  const [economizerOutletTemp, setEconomizerOutletTemp] = useState(null);
  const [boilerMakeup, setBoilerMakeup] = useState(null);
  const [boilerGas, setBoilerGas] = useState(null);
  const [boilerAmps, setBoilerAmps] = useState(null);
  const [steamProduction, setSteamProduction] = useState(null);
  const [preHeatedWater, setPreHeatedWater] = useState(null);
  const [feedwaterPumpAmps1, setFeedwaterPumpAmps1] = useState(null);
  const [feedwaterPumpAmps2, setFeedwaterPumpAmps2] = useState(null);

  // Page 3
  const [blowdownRate, setBlowdownRate] = useState('');
  const [blowdownFlashSteamRate, setBlowdownFlashSteamRate] = useState('');
  const [condensateFlashSteamRate, setCondensateFlashSteamRate] = useState('');
  const [blowdownWaterTemp, setBlowdownWaterTemp] = useState('');
  const [daVentSystem, setDaVentSystem] = useState('');
  const [steamPressure, setSteamPressure] = useState('');
  const [condensateReturnTemp, setCondensateReturnTemp] = useState('');
  const [feedwaterFlowRate, setFeedwaterFlowRate] = useState('');
  const [minAmpsToBeOn, setMinAmpsToBeOn] = useState('');

  useEffect(() => {
    if (show) {
      setPage(1);
      setEconomizer(initialConfig?.economizer ?? null);
      setEconomizerType(initialConfig?.economizerType ?? null);
      setBlowdownCoolingWater(initialConfig?.blowdownCoolingWater ?? null);
      setCondensateReturn(initialConfig?.condensateReturn ?? null);
      setDeaeratorSteamInjection(initialConfig?.deaeratorSteamInjection ?? null);
      setBoilerMakeupWaterTemp(initialConfig?.boilerMakeupWaterTemp ?? '');
      setFeedwaterTemp(initialConfig?.feedwaterTemp ?? '');
      setBlowdownRate(initialConfig?.blowdownRate ?? '');
      setBlowdownFlashSteamRate(initialConfig?.blowdownFlashSteamRate ?? '');
      setCondensateFlashSteamRate(initialConfig?.condensateFlashSteamRate ?? '');
      setBlowdownWaterTemp(initialConfig?.blowdownWaterTemp ?? '');
      setDaVentSystem(initialConfig?.daVentSystem ?? '');
      setSteamPressure(initialConfig?.steamPressure ?? '');
      setCondensateReturnTemp(initialConfig?.condensateReturnTemp ?? '');
      setFeedwaterFlowRate(initialConfig?.feedwaterFlowRate ?? '');
      setMinAmpsToBeOn(initialConfig?.minAmpsToBeOn ?? '');
      setBlowdownCoolingWaterMeter(null);
      setEconomizerInletTemp(null);
      setEconomizerOutletTemp(null);
      setBoilerMakeup(null);
      setBoilerGas(null);
      setBoilerAmps(null);
      setSteamProduction(null);
      setPreHeatedWater(null);
      setFeedwaterPumpAmps1(null);
      setFeedwaterPumpAmps2(null);

      const siteID = parseInt(window.location.href.split('/').slice(-1));

      axiosProtect.get(`/v2s/v2-sensors?site_id=${siteID}&utility=water`)
        .then(res => setWaterMeters(res.data.sensors ?? []))
        .catch(() => setWaterMeters([]));

      axiosProtect.get(`/v2s/v2-sensors?site_id=${siteID}&utility=electricity`)
        .then(res => {
          const cts = res.data.sensors ?? [];
          setCtSensors(cts.sort((a, b) => a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
        })
        .catch(() => setCtSensors([]));

      if (auth?.accessToken) {
        axiosProtect.get(`/v2s/v2-sensors?site_id=${siteID}&utility=gas`)
          .then(res => setGasSensors(res.data.sensors ?? []))
          .catch(() => setGasSensors([]));
      }
    }
  }, [show]);

  const isMakeupWater = economizer === 'Yes' && economizerType === 'Boiler Makeup Water';
  const isFeedwater = economizer === 'Yes' && economizerType === 'Boiler Feedwater';
  const isProcessStream = economizer === 'Yes' && economizerType === 'Process Stream';
  const page3Empty = economizer !== 'Yes' && blowdownCoolingWater !== 'Yes';
  const getNextPage = (p) => p === 2 && page3Empty ? 4 : p + 1;
  const getPrevPage = (p) => p === 4 && page3Empty ? 2 : p - 1;

  const page1Complete =
    economizer !== null &&
    (economizer !== 'Yes' || economizerType !== null) &&
    blowdownCoolingWater !== null &&
    condensateReturn !== null &&
    deaeratorSteamInjection !== null &&
    (!isMakeupWater || boilerMakeupWaterTemp !== '') &&
    (!isFeedwater || feedwaterTemp !== '');

  const page3Complete =
    blowdownRate !== '' &&
    blowdownFlashSteamRate !== '' &&
    condensateFlashSteamRate !== '' &&
    blowdownWaterTemp !== '' &&
    daVentSystem !== '' &&
    steamPressure !== '' &&
    condensateReturnTemp !== '';

  const handleConfirm = async () => {
    const responses = {
      economizer, economizerType, blowdownCoolingWater, condensateReturn, deaeratorSteamInjection,
      boilerMakeupWaterTemp, feedwaterTemp, heatCapacity,
      blowdownRate, blowdownFlashSteamRate, condensateFlashSteamRate, blowdownWaterTemp,
      daVentSystem, steamPressure, condensateReturnTemp,
      feedwaterFlowRate, minAmpsToBeOn,
      blowdownCoolingWaterMeterId: blowdownCoolingWaterMeter?.id ?? null,
      economizerInletTempId: economizerInletTemp?.id ?? null,
      economizerOutletTempId: economizerOutletTemp?.id ?? null,
      boilerMakeupId: boilerMakeup?.id ?? null,
      boilerGasId: boilerGas?.id ?? null,
      boilerAmpsId: boilerAmps?.id ?? null,
      steamProductionId: steamProduction?.id ?? null,
      preHeatedWaterId: preHeatedWater?.id ?? null,
      feedwaterPumpAmps1Id: feedwaterPumpAmps1?.id ?? null,
      feedwaterPumpAmps2Id: feedwaterPumpAmps2?.id ?? null,
    };

    setSubmitting(true);
    try {
      const siteID = parseInt(window.location.href.split('/').slice(-1));
      let imageUrl = null;

      const imageKey = getPFDImageKey(responses);
      if (imageKey) {
        try {
          const diagramSrc = require(`../../../../media/boiler-diagrams/${imageKey}.png`);
          const diagramRes = await fetch(diagramSrc);
          const diagramBlob = await diagramRes.blob();
          const diagramFile = new File([diagramBlob], `${imageKey}.png`, { type: 'image/png' });

          const formData = new FormData();
          formData.append('file', diagramFile);
          formData.append('upload_preset', 'stewwi');
          formData.append('cloud_name', process.env.REACT_APP_CLOUDINARY);

          const uploadRes = await fetch('https://api.cloudinary.com/v1_1/dw9sj7d5w/image/upload', {
            method: 'POST',
            body: formData,
          });
          const uploaded = await uploadRes.json();
          imageUrl = uploaded.url;
        } catch (err) {
          console.log(err);
        }
      }

      const payload = {
        ...responses,
        economizerType: economizer === 'Yes' ? (ECON_TYPE_KEY[economizerType] ?? economizerType) : economizerType,
        id: siteID,
        diagramImage: imageUrl,
      };
      console.log(payload)
      await axiosProtect.post('/b/boiler/add-boiler', payload);
      const site_id = siteID
      const sensors = {
        // Page 2
        boiler_makeup: boilerMakeup?.id ?? null,
        boiler_gas: boilerGas?.id ?? null,
        boiler_amps: boilerAmps?.id ?? null,
        steam_production: steamProduction?.id ?? null,
        // Page 3
        cooling_water: blowdownCoolingWater === 'Yes' ? (blowdownCoolingWaterMeter?.id ?? null) : null,
        inlet_temp: economizer === 'Yes' ? (economizerInletTemp?.id ?? null) : null,
        outlet_temp: economizer === 'Yes' ? (economizerOutletTemp?.id ?? null) : null,
        preheated_water: isProcessStream ? (preHeatedWater?.id ?? null) : null,
        pump_amps_1: isFeedwater ? (feedwaterPumpAmps1?.id ?? null) : null,
        pump_amps_2: isFeedwater ? (feedwaterPumpAmps2?.id ?? null) : null,
      };

      if (Object.values(sensors).some(id => id !== null)) {
        await axiosProtect.post('/b/boiler/add-boiler-sensor', { site_id, ...sensors });
      }
      onConfirm(payload);
    } catch (err) {
      console.log(err);
    } finally {
      setSubmitting(false);
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {page === 2 ? 'Assign Sensors' : `Add Boiler — Page ${page} of 5`}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {page === 1 && (
          <Form>
            <FieldRow tooltip={tooltips.economizer}>
              <Autocomplete
                options={yesNo}
                value={economizer}
                onChange={(_, val) => { setEconomizer(val); if (val !== 'Yes') setEconomizerType(null); }}
                renderInput={(params) => <TextField {...params} label="Economizer" variant="outlined" fullWidth />}
              />
            </FieldRow>

            {economizer === 'Yes' && (
              <FieldRow tooltip={tooltips.economizerType}>
                <Autocomplete
                  options={economizerTypeOptions}
                  value={economizerType}
                  onChange={(_, val) => setEconomizerType(val)}
                  renderInput={(params) => <TextField {...params} label="Type" variant="outlined" fullWidth />}
                />
              </FieldRow>
            )}

            {isMakeupWater && (
              <NumericField
                label="Boiler Makeup Water Temperature"
                value={boilerMakeupWaterTemp}
                onChange={setBoilerMakeupWaterTemp}
                unit="°F"
              />
            )}

            {isFeedwater && (
              <NumericField
                label="Feedwater Temperature (FWT)"
                value={feedwaterTemp}
                onChange={setFeedwaterTemp}
                unit="°F"
              />
            )}

            {isProcessStream && (
              <NumericField
                label="Heat Capacity of the Fluid"
                value={heatCapacity}
                onChange={setHeatCapacity}
                unit="°F"
              />
            )}

            <FieldRow tooltip={tooltips.blowdownCoolingWater}>
              <Autocomplete
                options={yesNo}
                value={blowdownCoolingWater}
                onChange={(_, val) => { setBlowdownCoolingWater(val); if (val !== 'Yes') setBlowdownCoolingWaterMeter(null); }}
                renderInput={(params) => <TextField {...params} label="Blowdown Cooling Water" variant="outlined" fullWidth />}
              />
            </FieldRow>

            <FieldRow tooltip={tooltips.condensateReturn}>
              <Autocomplete
                options={yesNo}
                value={condensateReturn}
                onChange={(_, val) => setCondensateReturn(val)}
                renderInput={(params) => <TextField {...params} label="Condensate Return" variant="outlined" fullWidth />}
              />
            </FieldRow>

            <FieldRow tooltip={tooltips.deaeratorSteamInjection}>
              <Autocomplete
                options={yesNo}
                value={deaeratorSteamInjection}
                onChange={(_, val) => setDeaeratorSteamInjection(val)}
                renderInput={(params) => <TextField {...params} label="Deaerator Steam Injection" variant="outlined" fullWidth />}
              />
            </FieldRow>
          </Form>
        )}

        {page === 2 && (
          <Form>
            <FieldRow>
              <Autocomplete
                options={waterMeters}
                value={boilerMakeup}
                onChange={(_, val) => setBoilerMakeup(val)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Boiler Makeup" variant="outlined" fullWidth />}
              />
            </FieldRow>

            <FieldRow>
              <Autocomplete
                options={gasSensors}
                value={boilerGas}
                onChange={(_, val) => setBoilerGas(val)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Boiler Gas" variant="outlined" fullWidth />}
              />
            </FieldRow>

            <FieldRow>
              <Autocomplete
                options={ctSensors}
                value={boilerAmps}
                onChange={(_, val) => setBoilerAmps(val)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Boiler Amps" variant="outlined" fullWidth />}
              />
            </FieldRow>

            <FieldRow>
              <Autocomplete
                options={waterMeters}
                value={steamProduction}
                onChange={(_, val) => setSteamProduction(val)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => <TextField {...params} label="Steam Production" variant="outlined" fullWidth />}
              />
            </FieldRow>
          </Form>
        )}

        {page === 3 && (
          <Form>
            {blowdownCoolingWater === 'Yes' && (
              <FieldRow>
                <Autocomplete
                  options={waterMeters}
                  value={blowdownCoolingWaterMeter}
                  onChange={(_, val) => setBlowdownCoolingWaterMeter(val)}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => <TextField {...params} label="Blowdown Cooling Water Meter" variant="outlined" fullWidth />}
                />
              </FieldRow>
            )}

            {economizer === 'Yes' && (
              <>
                <FieldRow>
                  <Autocomplete
                    options={[]}
                    value={economizerInletTemp}
                    onChange={(_, val) => setEconomizerInletTemp(val)}
                    renderInput={(params) => <TextField {...params} label="Economizer Inlet Temp" variant="outlined" fullWidth />}
                  />
                </FieldRow>
                <FieldRow>
                  <Autocomplete
                    options={[]}
                    value={economizerOutletTemp}
                    onChange={(_, val) => setEconomizerOutletTemp(val)}
                    renderInput={(params) => <TextField {...params} label="Economizer Outlet Temp" variant="outlined" fullWidth />}
                  />
                </FieldRow>
              </>
            )}

            {isProcessStream && (
              <FieldRow>
                <Autocomplete
                  options={waterMeters}
                  value={preHeatedWater}
                  onChange={(_, val) => setPreHeatedWater(val)}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(params) => <TextField {...params} label="Pre-heated Water" variant="outlined" fullWidth />}
                />
              </FieldRow>
            )}

            {isFeedwater && (
              <>
                <FieldRow>
                  <Autocomplete
                    options={ctSensors}
                    value={feedwaterPumpAmps1}
                    onChange={(_, val) => setFeedwaterPumpAmps1(val)}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => <TextField {...params} label="Feedwater Pump Amps 1" variant="outlined" fullWidth />}
                  />
                </FieldRow>
                <FieldRow>
                  <Autocomplete
                    options={ctSensors}
                    value={feedwaterPumpAmps2}
                    onChange={(_, val) => setFeedwaterPumpAmps2(val)}
                    getOptionLabel={(o) => o.name}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    renderInput={(params) => <TextField {...params} label="Feedwater Pump Amps 2" variant="outlined" fullWidth />}
                  />
                </FieldRow>
                <NumericField
                  label="Feedwater Flow Rate"
                  value={feedwaterFlowRate}
                  onChange={setFeedwaterFlowRate}
                  unit="GPM"
                />
                <NumericField
                  label="Minimum Amps to be ON"
                  value={minAmpsToBeOn}
                  onChange={setMinAmpsToBeOn}
                  unit="A"
                />
              </>
            )}
          </Form>
        )}

        {page === 4 && (
          <Form>
            <NumericField
              label="Boiler Blowdown Rate (% of boiler makeup)"
              value={blowdownRate}
              onChange={setBlowdownRate}
              unit="%"
              tooltip={tooltips.blowdownRate}
            />
            <NumericField
              label="Boiler Blowdown Flash Steam Rate"
              value={blowdownFlashSteamRate}
              onChange={setBlowdownFlashSteamRate}
              unit="%"
              tooltip={tooltips.blowdownFlashSteamRate}
            />
            <NumericField
              label="Condensate Flash Steam Rate"
              value={condensateFlashSteamRate}
              onChange={setCondensateFlashSteamRate}
              unit="%"
              tooltip={tooltips.condensateFlashSteamRate}
            />
            <NumericField
              label="Blowdown Water Temperature"
              value={blowdownWaterTemp}
              onChange={setBlowdownWaterTemp}
              unit="°F"
              tooltip={tooltips.blowdownWaterTemp}
            />
            <NumericField
              label="D/A Vent System"
              value={daVentSystem}
              onChange={setDaVentSystem}
              unit="lbs/hr"
              tooltip={tooltips.daVentSystem}
            />
            <NumericField
              label="Steam Pressure"
              value={steamPressure}
              onChange={setSteamPressure}
              unit="psig"
              tooltip={tooltips.steamPressure}
            />
            <NumericField
              label="Condensate Return Temperature"
              value={condensateReturnTemp}
              onChange={setCondensateReturnTemp}
              unit="°F"
              tooltip={tooltips.condensateReturnTemp}
            />
          </Form>
        )}

        {page === 5 && (
          <div>
            <ReviewSection title="System Configuration" />
            <ReviewRow label="Economizer" value={economizer} />
            {economizer === 'Yes' && <ReviewRow label="Economizer Type" value={economizerType} />}
            {isMakeupWater && <ReviewRow label="Boiler Makeup Water Temperature" value={boilerMakeupWaterTemp} unit="°F" />}
            {isFeedwater && <ReviewRow label="Feedwater Temperature (FWT)" value={feedwaterTemp} unit="°F" />}
            {isProcessStream && <ReviewRow label="Heat Capacity of the Fluid" value={heatCapacity} unit="°F" />}
            <ReviewRow label="Blowdown Cooling Water" value={blowdownCoolingWater} />
            <ReviewRow label="Condensate Return" value={condensateReturn} />
            <ReviewRow label="Deaerator Steam Injection" value={deaeratorSteamInjection} />

            <ReviewSection title="Assigned Sensors" />
            {blowdownCoolingWater === 'Yes' && blowdownCoolingWaterMeter && (
              <ReviewRow label="Blowdown Cooling Water Meter" value={blowdownCoolingWaterMeter.name} />
            )}
            {economizer === 'Yes' && economizerInletTemp && (
              <ReviewRow label="Economizer Inlet Temp" value={economizerInletTemp.name} />
            )}
            {economizer === 'Yes' && economizerOutletTemp && (
              <ReviewRow label="Economizer Outlet Temp" value={economizerOutletTemp.name} />
            )}
            {boilerMakeup && <ReviewRow label="Boiler Makeup" value={boilerMakeup.name} />}
            {boilerGas && <ReviewRow label="Boiler Gas" value={boilerGas.name} />}
            {boilerAmps && <ReviewRow label="Boiler Amps" value={boilerAmps.name} />}
            {steamProduction && <ReviewRow label="Steam Production" value={steamProduction.name} />}
            {isProcessStream && preHeatedWater && <ReviewRow label="Pre-heated Water" value={preHeatedWater.name} />}
            {isFeedwater && feedwaterPumpAmps1 && <ReviewRow label="Feedwater Pump Amps 1" value={feedwaterPumpAmps1.name} />}
            {isFeedwater && feedwaterPumpAmps2 && <ReviewRow label="Feedwater Pump Amps 2" value={feedwaterPumpAmps2.name} />}
            {isFeedwater && feedwaterFlowRate !== '' && <ReviewRow label="Feedwater Flow Rate" value={feedwaterFlowRate} unit="GPM" />}
            {isFeedwater && minAmpsToBeOn !== '' && <ReviewRow label="Minimum Amps to be ON" value={minAmpsToBeOn} unit="A" />}

            <ReviewSection title="Steam & Blowdown" />
            <ReviewRow label="Boiler Blowdown Rate" value={blowdownRate} unit="%" />
            <ReviewRow label="Boiler Blowdown Flash Steam Rate" value={blowdownFlashSteamRate} unit="%" />
            <ReviewRow label="Condensate Flash Steam Rate" value={condensateFlashSteamRate} unit="%" />
            <ReviewRow label="Blowdown Water Temperature" value={blowdownWaterTemp} unit="°F" />
            <ReviewRow label="D/A Vent System" value={daVentSystem} unit="lbs/hr" />
            <ReviewRow label="Steam Pressure" value={steamPressure} unit="psig" />
            <ReviewRow label="Condensate Return Temperature" value={condensateReturnTemp} unit="°F" />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {page > 1 && (
          <Button variant="secondary" onClick={() => setPage(getPrevPage(page))}>Back</Button>
        )}
        {page < 5 && (
          <Button
            variant="primary"
            onClick={() => setPage(getNextPage(page))}
            disabled={page === 1 ? !page1Complete : page === 4 ? !page3Complete : false}
          >
            Next
          </Button>
        )}
        {page === 5 && (
          <Button variant="primary" onClick={handleConfirm} disabled={submitting}>
            {submitting ? 'Saving...' : 'Confirm'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BoilerModal;
